import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  PlayIcon,
  VideoCameraIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import {
  subscribeToAttendanceRecords,
  getClassAttendanceRecords,
  computeUserAttendanceSummaries,
  exportUserAttendanceSummaryToExcel,
  exportAttendanceToExcel,
  getLocalDateString,
  subscribeToAttendanceAuditLogs,
  exportAttendanceAuditLogsToExcel,
  exportAllChatMessagesToExcel,
  getVideoDetails,
  getVideoChatMessages,
  exportVideoAttendanceToExcel,
  exportVideoAuditLogsToExcel,
  exportVideoChatMessagesToExcel,
} from '../../services/firebaseService';
import {
  extractYouTubeVideoId,
  formatYouTubeThumbnailUrl,
  getYouTubeEmbedUrl,
} from '../../utils/helpers';
import { TOTAL_REGISTERED_STUDENTS_COUNT } from '../../data/registeredCandidates';
import {
  ClassAttendanceRecord,
  UserAttendanceSummary,
  ClassAttendanceLogEntry,
  ClassVideoSegment,
  ClassChatMessage,
} from '../../types';

const formatSeconds = (secs: number): string => {
  if (!secs || secs <= 0) return '0m';
  const m = Math.floor(secs / 60);
  const h = Math.floor(m / 60);
  if (h > 0) {
    const remM = m % 60;
    return `${h}h ${remM}m`;
  }
  return `${m}m`;
};

const formatTimeOnly = (rawTs: any): string => {
  if (!rawTs) return '—';
  try {
    const d = rawTs.toDate ? rawTs.toDate() : new Date(rawTs);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

export interface UserAttendanceAnalyticsProps {
  initialViewMode?: 'users' | 'sessions' | 'audit' | 'videos';
  initialVideoFilter?: string;
}

export const UserAttendanceAnalytics: React.FC<UserAttendanceAnalyticsProps> = ({
  initialViewMode,
  initialVideoFilter,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlMode = searchParams.get('mode') as 'users' | 'sessions' | 'audit' | 'videos' | null;
  const urlVideo = searchParams.get('video');

  const [records, setRecords] = useState<ClassAttendanceRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'users' | 'sessions' | 'audit' | 'videos'>(
    urlMode || initialViewMode || 'videos'
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [timeframeFilter, setTimeframeFilter] = useState<'all' | 'today' | 'weekly' | 'monthly' | 'live'>('all');
  const [sortBy, setSortBy] = useState<'total' | 'monthly' | 'weekly' | 'daily' | 'days' | 'name'>('total');
  const [selectedUser, setSelectedUser] = useState<UserAttendanceSummary | null>(null);

  // Daily raw log filter
  const [sessionDateFilter, setSessionDateFilter] = useState<string>('');
  const [sessionVideoFilter, setSessionVideoFilter] = useState<string>(urlVideo || initialVideoFilter || 'all');
  const [exportingMessages, setExportingMessages] = useState<boolean>(false);

  // Audit trail state
  const [auditLogs, setAuditLogs] = useState<ClassAttendanceLogEntry[]>([]);
  const [auditDateFilter, setAuditDateFilter] = useState<string>(getLocalDateString());
  const [auditVideoFilter, setAuditVideoFilter] = useState<string>(urlVideo || initialVideoFilter || 'all');
  const [auditEventFilter, setAuditEventFilter] = useState<string>('all');
  const [auditSearchQuery, setAuditSearchQuery] = useState<string>('');

  // Video segmentation & inspector state
  const [inspectingVideo, setInspectingVideo] = useState<ClassVideoSegment | null>(null);
  const [inspectingChatMessages, setInspectingChatMessages] = useState<ClassChatMessage[]>([]);
  const [loadingVideoChat, setLoadingVideoChat] = useState<boolean>(false);
  const [videoSearchQuery, setVideoSearchQuery] = useState<string>('');
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [inspectingSubTab, setInspectingSubTab] = useState<'overview' | 'attendance' | 'chat' | 'logs'>('overview');

  const handleViewModeChange = (mode: 'users' | 'sessions' | 'audit' | 'videos') => {
    setViewMode(mode);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('mode', mode);
      return next;
    });
  };


  // Fetch full records (all time)
  const fetchAllAttendance = async () => {
    setRefreshing(true);
    try {
      const data = await getClassAttendanceRecords();
      setRecords(data);
    } catch (err) {
      console.error('Failed to load attendance records:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Subscribe to real-time changes (delivers initial records immediately + updates on change, avoiding double-fetch)
    const unsub = subscribeToAttendanceRecords((newRecords) => {
      setRecords(newRecords);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Subscribe to real-time audit logs
  useEffect(() => {
    const unsub = subscribeToAttendanceAuditLogs(
      (logs) => {
        setAuditLogs(logs);
      },
      auditDateFilter || undefined
    );
    return () => unsub();
  }, [auditDateFilter]);

  // Compute aggregated per-user summaries
  const userSummaries = useMemo(() => {
    return computeUserAttendanceSummaries(records);
  }, [records]);

  // Overall metric cards
  const metrics = useMemo(() => {
    const totalUsers = userSummaries.length;
    const nowMs = Date.now();
    const todayStr = getLocalDateString(new Date(nowMs));

    const todaySeconds = records
      .filter((r) => r.sessionDate === todayStr)
      .reduce((sum, r) => sum + (r.totalWatchSeconds || 0), 0);

    const sevenDaysAgo = getLocalDateString(new Date(nowMs - 7 * 24 * 60 * 60 * 1000));
    const weeklySeconds = records
      .filter((r) => (r.sessionDate || '') >= sevenDaysAgo)
      .reduce((sum, r) => sum + (r.totalWatchSeconds || 0), 0);

    const thirtyDaysAgo = getLocalDateString(new Date(nowMs - 30 * 24 * 60 * 60 * 1000));
    const monthlySeconds = records
      .filter((r) => (r.sessionDate || '') >= thirtyDaysAgo)
      .reduce((sum, r) => sum + (r.totalWatchSeconds || 0), 0);

    const totalSeconds = records.reduce((sum, r) => sum + (r.totalWatchSeconds || 0), 0);
    const activeLiveCount = userSummaries.filter((u) => u.isCurrentlyActive).length;

    return {
      totalUsers,
      todaySeconds,
      weeklySeconds,
      monthlySeconds,
      totalSeconds,
      activeLiveCount,
    };
  }, [records, userSummaries]);

  // Filtered and sorted user summaries
  const filteredUsers = useMemo(() => {
    return userSummaries
      .filter((u) => {
        // Search query
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const matchName = (u.studentName || '').toLowerCase().includes(q);
          const matchReg = (u.registrationNumber || '').toLowerCase().includes(q);
          const matchSuffix = (u.regNoSuffix || '').toLowerCase().includes(q);
          if (!matchName && !matchReg && !matchSuffix) return false;
        }

        // Timeframe filter
        if (timeframeFilter === 'today') return u.dailySeconds > 0;
        if (timeframeFilter === 'weekly') return u.weeklySeconds > 0;
        if (timeframeFilter === 'monthly') return u.monthlySeconds > 0;
        if (timeframeFilter === 'live') return u.isCurrentlyActive;

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'daily') return b.dailySeconds - a.dailySeconds;
        if (sortBy === 'weekly') return b.weeklySeconds - a.weeklySeconds;
        if (sortBy === 'monthly') return b.monthlySeconds - a.monthlySeconds;
        if (sortBy === 'days') return b.daysAttended - a.daysAttended;
        if (sortBy === 'name') return a.studentName.localeCompare(b.studentName);
        return b.totalSeconds - a.totalSeconds;
      });
  }, [userSummaries, searchQuery, timeframeFilter, sortBy]);

  // Aggregated Video Segments discovered across all records and logs
  const videoSegments = useMemo<ClassVideoSegment[]>(() => {
    const videoMap: Record<string, ClassVideoSegment> = {};

    // 1. Baseline known training lectures
    const baselineVids = ['MIvyvX0m4_Q', 'nKB_iMgwlWg', 'lMgDVbzbMx8'];
    baselineVids.forEach((vid) => {
      const d = getVideoDetails(vid);
      videoMap[vid] = {
        videoId: vid,
        videoUrl: d.videoUrl,
        title: d.sessionName,
        topic: d.topic,
        instructor: d.instructor,
        attendeesCount: 0,
        totalWatchSeconds: 0,
        chatMessagesCount: 0,
        viewLogsCount: 0,
      };
    });

    // 2. Discover from attendance records
    records.forEach((r) => {
      const vid = r.videoId || extractYouTubeVideoId(r.streamUrl);
      if (!vid) return;
      if (!videoMap[vid]) {
        const d = getVideoDetails(vid);
        videoMap[vid] = {
          videoId: vid,
          videoUrl: r.streamUrl || d.videoUrl,
          title: r.liveLabel || d.sessionName,
          topic: d.topic,
          date: r.sessionDate,
          instructor: d.instructor,
          attendeesCount: 0,
          totalWatchSeconds: 0,
          chatMessagesCount: 0,
          viewLogsCount: 0,
        };
      }
      videoMap[vid].attendeesCount = (videoMap[vid].attendeesCount || 0) + 1;
      videoMap[vid].totalWatchSeconds = (videoMap[vid].totalWatchSeconds || 0) + (r.totalWatchSeconds || 0);
      if (r.sessionDate && !videoMap[vid].date) videoMap[vid].date = r.sessionDate;
    });

    // 3. Discover from audit view logs
    auditLogs.forEach((l) => {
      const vid = l.videoId || extractYouTubeVideoId(l.streamUrl);
      if (!vid) return;
      if (!videoMap[vid]) {
        const d = getVideoDetails(vid);
        videoMap[vid] = {
          videoId: vid,
          videoUrl: l.streamUrl || d.videoUrl,
          title: l.liveLabel || d.sessionName,
          topic: d.topic,
          date: l.sessionDate,
          instructor: d.instructor,
          attendeesCount: 0,
          totalWatchSeconds: 0,
          chatMessagesCount: 0,
          viewLogsCount: 0,
        };
      }
      videoMap[vid].viewLogsCount = (videoMap[vid].viewLogsCount || 0) + 1;
    });

    return Object.values(videoMap);
  }, [records, auditLogs]);

  // Filtered raw sessions with video segmentation support
  const filteredSessions = useMemo(() => {
    return records.filter((r) => {
      if (sessionDateFilter && r.sessionDate !== sessionDateFilter) return false;
      if (sessionVideoFilter !== 'all') {
        const vid = r.videoId || extractYouTubeVideoId(r.streamUrl);
        if (vid !== sessionVideoFilter) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return (
          (r.studentName || '').toLowerCase().includes(q) ||
          (r.registrationNumber || '').toLowerCase().includes(q) ||
          (r.regNoSuffix || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [records, sessionDateFilter, sessionVideoFilter, searchQuery]);

  // Filtered audit logs with video segmentation support
  const filteredAuditLogs = useMemo(() => {
    return auditLogs.filter((log) => {
      if (auditEventFilter !== 'all' && log.eventType !== auditEventFilter) return false;
      if (auditVideoFilter !== 'all') {
        const vid = log.videoId || extractYouTubeVideoId(log.streamUrl);
        if (vid !== auditVideoFilter) return false;
      }
      if (auditSearchQuery.trim()) {
        const q = auditSearchQuery.toLowerCase();
        const matchName = (log.studentName || '').toLowerCase().includes(q);
        const matchReg = (log.registrationNumber || '').toLowerCase().includes(q);
        const matchSuffix = (log.regNoSuffix || '').toLowerCase().includes(q);
        const matchTopic = (log.liveLabel || '').toLowerCase().includes(q);
        if (!matchName && !matchReg && !matchSuffix && !matchTopic) return false;
      }
      return true;
    });
  }, [auditLogs, auditEventFilter, auditVideoFilter, auditSearchQuery]);

  // Filtered video segments based on search query
  const filteredVideoSegments = useMemo(() => {
    if (!videoSearchQuery.trim()) return videoSegments;
    const q = videoSearchQuery.toLowerCase();
    return videoSegments.filter(
      (v) =>
        v.title.toLowerCase().includes(q) ||
        v.videoId.toLowerCase().includes(q) ||
        (v.topic || '').toLowerCase().includes(q) ||
        (v.instructor || '').toLowerCase().includes(q) ||
        (v.date || '').includes(q)
    );
  }, [videoSegments, videoSearchQuery]);

  const handleInspectVideo = async (seg: ClassVideoSegment, subTab: 'overview' | 'attendance' | 'chat' | 'logs' = 'overview') => {
    setInspectingVideo(seg);
    setInspectingSubTab(subTab);
    setLoadingVideoChat(true);
    try {
      const msgs = await getVideoChatMessages(seg.videoId);
      setInspectingChatMessages(msgs);
    } catch (err) {
      console.error('Failed to load video chat messages:', err);
    } finally {
      setLoadingVideoChat(false);
    }
  };

  const handleExportUsers = () => {
    exportUserAttendanceSummaryToExcel(
      filteredUsers,
      `Kreed_User_Attendance_Summary_${getLocalDateString()}.xlsx`
    );
  };

  const handleExportSessions = () => {
    exportAttendanceToExcel(
      filteredSessions,
      `Kreed_Class_Sessions_${sessionDateFilter || 'All'}.xlsx`
    );
  };

  const handleExportAuditLogs = () => {
    exportAttendanceAuditLogsToExcel(
      filteredAuditLogs,
      `Kreed_Attendance_Audit_Logs_${auditDateFilter || 'All'}.xlsx`
    );
  };

  const handleExportAllChatMessages = async () => {
    setExportingMessages(true);
    try {
      await exportAllChatMessagesToExcel('Kreed_Live_Class_Videos_Messages_Report.xlsx');
    } catch (err: any) {
      alert(err?.message || 'Failed to export chat messages report.');
    } finally {
      setExportingMessages(false);
    }
  };


  return (
    <div className="space-y-6">
      {/* ── Top Header & Tab Controls ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 tracking-tight flex items-center space-x-2">
            <span>Student Attendance & Viewing Analytics</span>
            {metrics.activeLiveCount > 0 && (
              <span className="flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping mr-1" />
                <span>{metrics.activeLiveCount} Watching Live</span>
              </span>
            )}
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Aggregated Daily, Weekly, and Monthly viewing duration, session logs, and real-time audit trail.
          </p>
        </div>

        <div className="flex items-center space-x-2.5 flex-wrap">
          {/* View mode switcher */}
          <div className="bg-gray-100 p-1 rounded-xl flex items-center space-x-1 border border-gray-200 flex-wrap gap-y-1">
            <button
              type="button"
              onClick={() => handleViewModeChange('videos')}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center space-x-1.5 ${
                viewMode === 'videos'
                  ? 'bg-rose-600 text-white shadow-sm ring-2 ring-rose-300'
                  : 'text-rose-700 hover:text-rose-900 hover:bg-rose-50'
              }`}
            >
              <span>📹 Video Segments</span>
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                viewMode === 'videos' ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-800'
              }`}>
                {videoSegments.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleViewModeChange('users')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'users'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              👥 Per-User Analytics
            </button>
            <button
              type="button"
              onClick={() => handleViewModeChange('sessions')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === 'sessions'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              📄 Session Logs (Daily)
            </button>
            <button
              type="button"
              onClick={() => handleViewModeChange('audit')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center space-x-1.5 ${
                viewMode === 'audit'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'text-indigo-700 hover:text-indigo-900 hover:bg-indigo-50'
              }`}
            >
              <span>📜 Real-Time Audit Trail</span>
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            </button>
          </div>

          <button
            type="button"
            onClick={fetchAllAttendance}
            disabled={refreshing}
            className="inline-flex items-center space-x-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-xs font-semibold shadow-sm transition-colors disabled:opacity-50"
            title="Refresh from Firestore"
          >
            <ArrowPathIcon className={`w-4 h-4 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          {viewMode === 'users' ? (
            <button
              type="button"
              onClick={handleExportUsers}
              className="inline-flex items-center space-x-1.5 bg-primary-600 hover:bg-primary-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold shadow-sm transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Export User Summary</span>
            </button>
          ) : viewMode === 'sessions' ? (
            <button
              type="button"
              onClick={handleExportSessions}
              className="inline-flex items-center space-x-1.5 bg-primary-600 hover:bg-primary-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold shadow-sm transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Export Session Logs</span>
            </button>
          ) : viewMode === 'audit' ? (
            <button
              type="button"
              onClick={handleExportAuditLogs}
              className="inline-flex items-center space-x-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold shadow-sm transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Export Audit Trail ({filteredAuditLogs.length})</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleExportAllChatMessages}
              disabled={exportingMessages}
              className="inline-flex items-center space-x-1.5 bg-rose-600 hover:bg-rose-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold shadow-sm transition-colors disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Export All Videos Report</span>
            </button>
          )}


          {/* All Videos Chat Messages Report Export */}
          <button
            type="button"
            onClick={handleExportAllChatMessages}
            disabled={exportingMessages}
            className="inline-flex items-center space-x-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold shadow-sm transition-colors disabled:opacity-50"
            title="Download comprehensive multi-sheet Excel audit of all messages sent on all videos"
          >
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
            <span>{exportingMessages ? 'Generating Report...' : '📥 All Videos Chat Report (Excel)'}</span>
          </button>
        </div>
      </div>

      {/* ── Top Metric Cards (Daily / Weekly / Monthly / Total) ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {/* Card 1: Master Registry & Unique Students */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50/60 border border-blue-200/80 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Total Registered</p>
            <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center">
              <UserGroupIcon className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl sm:text-3xl font-black text-blue-950 mt-1">
            {TOTAL_REGISTERED_STUDENTS_COUNT}{' '}
            <span className="text-sm font-semibold text-blue-600">({metrics.totalUsers} attended)</span>
          </p>
          <p className="text-[11px] text-blue-600/90 mt-0.5">745 verified candidates in master registry</p>
        </div>

        {/* Card 2: Today (Daily) */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50/60 border border-emerald-200/80 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Today (Daily)</p>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
              <ClockIcon className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl sm:text-3xl font-black text-emerald-950 mt-1">
            {formatSeconds(metrics.todaySeconds)}
          </p>
          <p className="text-[11px] text-emerald-700 font-semibold mt-0.5">
            {metrics.activeLiveCount} currently live watching
          </p>
        </div>

        {/* Card 3: This Week (Weekly) */}
        <div className="bg-gradient-to-br from-purple-50 to-violet-50/60 border border-purple-200/80 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-purple-700">Past 7 Days (Weekly)</p>
            <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-600 flex items-center justify-center">
              <CalendarDaysIcon className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl sm:text-3xl font-black text-purple-950 mt-1">
            {formatSeconds(metrics.weeklySeconds)}
          </p>
          <p className="text-[11px] text-purple-700 mt-0.5">Accumulated weekly watch time</p>
        </div>

        {/* Card 4: This Month (Monthly) */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50/60 border border-amber-200/80 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-amber-800">Past 30 Days (Monthly)</p>
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-700 flex items-center justify-center">
              <ChartBarIcon className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl sm:text-3xl font-black text-amber-950 mt-1">
            {formatSeconds(metrics.monthlySeconds)}
          </p>
          <p className="text-[11px] text-amber-800 mt-0.5">Monthly viewing volume</p>
        </div>
      </div>

      {/* ── Search, Filters & Sort Bar ── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-3.5 sm:p-4 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by student name, registration no, or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400 transition"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
            >
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* User mode filters */}
        {viewMode === 'users' ? (
          <div className="flex items-center space-x-2 flex-wrap gap-y-2">
            {/* Timeframe pill filter */}
            <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-xl text-xs font-semibold">
              <button
                type="button"
                onClick={() => setTimeframeFilter('all')}
                className={`px-2.5 py-1 rounded-lg transition-all ${
                  timeframeFilter === 'all'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All Time
              </button>
              <button
                type="button"
                onClick={() => setTimeframeFilter('today')}
                className={`px-2.5 py-1 rounded-lg transition-all ${
                  timeframeFilter === 'today'
                    ? 'bg-white text-emerald-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setTimeframeFilter('weekly')}
                className={`px-2.5 py-1 rounded-lg transition-all ${
                  timeframeFilter === 'weekly'
                    ? 'bg-white text-purple-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Past 7d
              </button>
              <button
                type="button"
                onClick={() => setTimeframeFilter('monthly')}
                className={`px-2.5 py-1 rounded-lg transition-all ${
                  timeframeFilter === 'monthly'
                    ? 'bg-white text-amber-800 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Past 30d
              </button>
              <button
                type="button"
                onClick={() => setTimeframeFilter('live')}
                className={`px-2.5 py-1 rounded-lg transition-all flex items-center space-x-1 ${
                  timeframeFilter === 'live'
                    ? 'bg-white text-emerald-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span>Live</span>
              </button>
            </div>

            {/* Sort selector */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-2.5 py-1.5 border border-gray-300 rounded-xl text-xs font-medium text-gray-700 focus:ring-1 focus:ring-primary-500 bg-white"
            >
              <option value="total">Sort: Highest Lifetime Time</option>
              <option value="monthly">Sort: Highest Past 30 Days</option>
              <option value="weekly">Sort: Highest Past 7 Days</option>
              <option value="daily">Sort: Highest Today</option>
              <option value="days">Sort: Most Days Attended</option>
              <option value="name">Sort: Name (A-Z)</option>
            </select>
          </div>
        ) : (
          /* Session logs date filter */
          <div className="flex items-center space-x-2">
            <label className="text-xs font-medium text-gray-700 whitespace-nowrap">Filter Date:</label>
            <input
              type="date"
              value={sessionDateFilter}
              onChange={(e) => setSessionDateFilter(e.target.value)}
              className="px-2.5 py-1.5 border border-gray-300 rounded-xl text-xs focus:ring-1 focus:ring-primary-500"
            />
            {sessionDateFilter && (
              <button
                type="button"
                onClick={() => setSessionDateFilter('')}
                className="text-xs text-primary-600 hover:text-primary-700 font-semibold"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Table View: Mode 1: Per-User Summary ── */}
      {viewMode === 'users' && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs divide-y divide-gray-200">
              <thead className="bg-gray-50/80 text-gray-600 font-bold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="px-3.5 py-3 text-center w-12">#</th>
                  <th className="px-4 py-3">Student Info</th>
                  <th className="px-3.5 py-3 hidden sm:table-cell">Reg No</th>
                  <th className="px-3.5 py-3 text-emerald-800 bg-emerald-50/40">Today (Daily)</th>
                  <th className="px-3.5 py-3 text-purple-800 bg-purple-50/30">Past 7d (Weekly)</th>
                  <th className="px-3.5 py-3 text-amber-800 bg-amber-50/30">Past 30d (Monthly)</th>
                  <th className="px-3.5 py-3 font-black text-gray-900">Total Lifetime</th>
                  <th className="px-3.5 py-3 text-center">Days</th>
                  <th className="px-3.5 py-3 hidden md:table-cell">Last Active</th>
                  <th className="px-3.5 py-3 text-right">History</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center space-y-2">
                        <ArrowPathIcon className="w-6 h-6 text-primary-600 animate-spin" />
                        <span className="text-xs font-semibold">Aggregating student viewing metrics...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center space-y-1">
                        <UserGroupIcon className="w-8 h-8 text-gray-300 mb-1" />
                        <span className="font-semibold text-gray-700 text-sm">No student attendance records match filter</span>
                        <span className="text-xs text-gray-400">Try adjusting your search query or timeframe filter.</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user, idx) => {
                    const hasToday = user.dailySeconds > 0;
                    return (
                      <tr
                        key={user.registrationNumber}
                        className={`transition-colors hover:bg-gray-50/80 ${
                          user.isCurrentlyActive ? 'bg-emerald-50/30' : ''
                        }`}
                      >
                        <td className="px-3.5 py-3 text-center text-gray-400 font-mono text-[11px]">
                          {idx + 1}
                        </td>

                        {/* Student Name */}
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            {user.isCurrentlyActive ? (
                              <span
                                className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0"
                                title="Currently Watching Live"
                              />
                            ) : hasToday ? (
                              <span
                                className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"
                                title="Watched Today"
                              />
                            ) : null}
                            <div>
                              <div className="font-bold text-gray-900 text-xs sm:text-sm leading-tight">
                                {user.studentName}
                              </div>
                              <div className="text-[10px] text-gray-400 font-mono mt-0.5">
                                ID: {user.regNoSuffix}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Reg No */}
                        <td className="px-3.5 py-3 font-mono text-xs text-gray-600 hidden sm:table-cell">
                          {user.registrationNumber}
                        </td>

                        {/* Daily */}
                        <td className="px-3.5 py-3 bg-emerald-50/20">
                          {hasToday ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              {formatSeconds(user.dailySeconds)}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>

                        {/* Weekly */}
                        <td className="px-3.5 py-3 bg-purple-50/15">
                          {user.weeklySeconds > 0 ? (
                            <span className="font-semibold text-purple-900 text-xs">
                              {formatSeconds(user.weeklySeconds)}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>

                        {/* Monthly */}
                        <td className="px-3.5 py-3 bg-amber-50/15">
                          {user.monthlySeconds > 0 ? (
                            <span className="font-semibold text-amber-900 text-xs">
                              {formatSeconds(user.monthlySeconds)}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">—</span>
                          )}
                        </td>

                        {/* Lifetime Total */}
                        <td className="px-3.5 py-3 font-black text-gray-900 text-xs sm:text-sm">
                          {formatSeconds(user.totalSeconds)}
                        </td>

                        {/* Days Attended */}
                        <td className="px-3.5 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-gray-100 text-gray-700">
                            {user.daysAttended} {user.daysAttended === 1 ? 'day' : 'days'}
                          </span>
                        </td>

                        {/* Last Active */}
                        <td className="px-3.5 py-3 text-gray-500 text-[11px] whitespace-nowrap hidden md:table-cell">
                          <div>{user.lastSeenDate}</div>
                          {user.lastSeenTimestamp && (
                            <div className="text-[10px] text-gray-400">
                              {formatTimeOnly(user.lastSeenTimestamp)}
                            </div>
                          )}
                        </td>

                        {/* Action: Open History Modal */}
                        <td className="px-3.5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedUser(user)}
                            className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold text-xs transition border border-indigo-200"
                          >
                            <span>History</span>
                            <ChevronRightIcon className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className="p-3 bg-gray-50/60 border-t border-gray-200 text-xs text-gray-500 flex justify-between items-center">
            <span>Showing <strong>{filteredUsers.length}</strong> of <strong>{userSummaries.length}</strong> students</span>
            <span className="text-[11px] text-gray-400">Tip: Click "History" on any row to view day-by-day logs for that candidate</span>
          </div>
        </div>
      )}

      {/* ── Table View: Mode 2: Raw Daily Session Logs ── */}
      {viewMode === 'sessions' && (
        <div className="space-y-3">
          {/* Session Filters Bar */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="flex items-center space-x-2 flex-wrap gap-y-2">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Filter Date:
              </span>
              <input
                type="date"
                value={sessionDateFilter}
                onChange={(e) => setSessionDateFilter(e.target.value)}
                className="px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {sessionDateFilter && (
                <button
                  type="button"
                  onClick={() => setSessionDateFilter('')}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                >
                  Clear Date
                </button>
              )}

              {/* YouTube Video Link Filter */}
              <div className="flex items-center space-x-1.5 ml-0 sm:ml-2">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Video Link:
                </span>
                <select
                  value={sessionVideoFilter}
                  onChange={(e) => setSessionVideoFilter(e.target.value)}
                  className="px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-rose-500 max-w-xs"
                >
                  <option value="all">All Videos ({videoSegments.length})</option>
                  {videoSegments.map((seg) => (
                    <option key={seg.videoId} value={seg.videoId}>
                      📹 {seg.videoId} — {seg.title.slice(0, 32)}...
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="text-xs text-gray-500">
              Showing <strong>{filteredSessions.length}</strong> session records
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs divide-y divide-gray-200">
                <thead className="bg-gray-50 text-gray-700 font-semibold uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="px-3 py-3 w-12 text-center">#</th>
                    <th className="px-4 py-3">Student Name</th>
                    <th className="px-3 py-3">Reg No</th>
                    <th className="px-3 py-3">Session Date</th>
                    <th className="px-3 py-3">YouTube Video</th>
                    <th className="px-3 py-3">Stream Topic</th>
                    <th className="px-3 py-3">First Joined</th>
                    <th className="px-3 py-3">Last Active</th>
                    <th className="px-3 py-3">Watch Time</th>
                    <th className="px-3 py-3 text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-700">
                  {filteredSessions.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-12 text-center text-gray-500">
                        No session records found matching your filters.
                      </td>
                    </tr>
                  ) : (
                    filteredSessions.map((record, idx) => {
                      const mins = Math.round((record.totalWatchSeconds || 0) / 60);
                      const vidId = record.videoId || extractYouTubeVideoId(record.streamUrl);
                      return (
                        <tr key={record.id || idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-3 text-center text-gray-400">{idx + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">{record.studentName}</div>
                            <div className="text-[10px] text-gray-400 font-mono">ID: {record.regNoSuffix}</div>
                          </td>
                          <td className="px-3 py-3 font-mono text-gray-600">{record.registrationNumber}</td>
                          <td className="px-3 py-3 font-medium whitespace-nowrap text-gray-800">{record.sessionDate}</td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {vidId ? (
                              <div className="flex items-center space-x-1.5">
                                <a
                                  href={`https://www.youtube.com/watch?v=${vidId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[11px] font-mono font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition"
                                  title="Open YouTube video in new tab"
                                >
                                  <span>📹</span>
                                  <span>{vidId}</span>
                                </a>
                                <button
                                  type="button"
                                  onClick={() => setPreviewVideoUrl(`https://www.youtube.com/watch?v=${vidId}`)}
                                  className="text-gray-400 hover:text-gray-700 text-[10px]"
                                  title="Quick Preview"
                                >
                                  👁️
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 font-mono text-[10px]">General</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-gray-600 max-w-xs truncate">{record.liveLabel || 'General Training Session'}</td>
                          <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{formatTimeOnly(record.firstJoinedAt)}</td>
                          <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{formatTimeOnly(record.lastSeenAt)}</td>
                          <td className="px-3 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                              {formatSeconds(record.totalWatchSeconds || 0)} ({mins}m)
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700">
                              Attended
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}


      {/* ── Table View: Mode 3: Real-Time Attendance Audit Trail ── */}
      {viewMode === 'audit' && (
        <div className="space-y-4">
          {/* Audit Controls & Filters */}
          <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-2 flex-wrap gap-y-2">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Filter Session Date:
                </span>
                <input
                  type="date"
                  value={auditDateFilter}
                  onChange={(e) => setAuditDateFilter(e.target.value)}
                  className="px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  onClick={() => setAuditDateFilter(getLocalDateString())}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                    auditDateFilter === getLocalDateString()
                      ? 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => setAuditDateFilter('')}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                    auditDateFilter === ''
                      ? 'bg-indigo-100 text-indigo-800 border border-indigo-300'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  All Dates
                </button>
              </div>

              {/* YouTube Video Link Filter */}
              <div className="flex items-center space-x-1.5">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Video Link:
                </span>
                <select
                  value={auditVideoFilter}
                  onChange={(e) => setAuditVideoFilter(e.target.value)}
                  className="px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 max-w-xs"
                >
                  <option value="all">All Videos ({videoSegments.length})</option>
                  {videoSegments.map((seg) => (
                    <option key={seg.videoId} value={seg.videoId}>
                      📹 {seg.videoId} — {seg.title.slice(0, 28)}...
                    </option>
                  ))}
                </select>
              </div>

              {/* Event Type Filter */}
              <div className="flex items-center space-x-2">
                <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Event:
                </span>
                <select
                  value={auditEventFilter}
                  onChange={(e) => setAuditEventFilter(e.target.value)}
                  className="px-3 py-1.5 bg-gray-50 border border-gray-300 rounded-xl text-xs font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All Events ({auditLogs.length})</option>
                  <option value="login">🔑 Logins</option>
                  <option value="session_start">🚀 Dashboard Entries</option>
                  <option value="heartbeat">💓 Activity Heartbeats</option>
                  <option value="tab_visible">👁️ Tab Foreground</option>
                  <option value="tab_hidden">💤 Tab Background</option>
                  <option value="page_leave">🚪 Tab Closed / Leave</option>
                  <option value="logout">🔒 Logouts</option>
                  <option value="offline_flush">⚡ Offline Synced</option>
                </select>
              </div>
            </div>

            {/* Search Bar for Audit Trail */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                <MagnifyingGlassIcon className="w-4 h-4" />
              </div>
              <input
                type="text"
                value={auditSearchQuery}
                onChange={(e) => setAuditSearchQuery(e.target.value)}
                placeholder="Search audit trail by student name, registration number, or stream topic..."
                className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {auditSearchQuery && (
                <button
                  type="button"
                  onClick={() => setAuditSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Quick Stat Counter Pills */}
            <div className="flex items-center space-x-2 pt-1 overflow-x-auto text-[11px]">
              <span className="px-2.5 py-1 rounded-lg bg-gray-100 font-semibold text-gray-700">
                Total Logs: <strong>{filteredAuditLogs.length}</strong>
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 font-semibold border border-emerald-200">
                🔑 Logins: <strong>{filteredAuditLogs.filter((l) => l.eventType === 'login').length}</strong>
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-800 font-semibold border border-indigo-200">
                💓 Pulses: <strong>{filteredAuditLogs.filter((l) => l.eventType === 'heartbeat').length}</strong>
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 font-semibold border border-amber-200">
                💤 In Background: <strong>{filteredAuditLogs.filter((l) => l.eventType === 'tab_hidden').length}</strong>
              </span>
              <span className="px-2.5 py-1 rounded-lg bg-purple-50 text-purple-800 font-semibold border border-purple-200">
                ⚡ Offline Recovered: <strong>{filteredAuditLogs.filter((l) => l.eventType === 'offline_flush').length}</strong>
              </span>
            </div>
          </div>

          {/* Audit Logs Table */}
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs divide-y divide-gray-200">
                <thead className="bg-gray-50 text-gray-700 font-semibold uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="px-3 py-3 w-12 text-center">#</th>
                    <th className="px-3 py-3 whitespace-nowrap">Timestamp</th>
                    <th className="px-3 py-3">Event Type</th>
                    <th className="px-4 py-3">Student Name</th>
                    <th className="px-3 py-3">Reg No</th>
                    <th className="px-3 py-3">YouTube Video</th>
                    <th className="px-3 py-3">Watch Pulse</th>
                    <th className="px-3 py-3">Lecture Topic</th>
                    <th className="px-3 py-3">Visibility</th>
                    <th className="px-3 py-3">Device / Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-gray-700">
                  {filteredAuditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-16 text-center text-gray-500">
                        <div className="max-w-xs mx-auto space-y-2">
                          <DocumentTextIcon className="w-8 h-8 text-gray-300 mx-auto" />
                          <p className="font-semibold text-gray-700">No attendance audit logs found</p>
                          <p className="text-[11px] text-gray-400">
                            {auditDateFilter
                              ? `No event logs recorded for ${auditDateFilter}. Try changing the date filter or switching to All Dates.`
                              : 'No activity logs have been recorded yet.'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredAuditLogs.map((log, idx) => {
                      const timeStr = log.timestampStr || (log.timestamp?.toDate ? log.timestamp.toDate().toLocaleTimeString('en-IN') : '—');
                      const dateStr = log.sessionDate || (log.timestamp?.toDate ? log.timestamp.toDate().toISOString().split('T')[0] : '');
                      const vidId = log.videoId || extractYouTubeVideoId(log.streamUrl);
                      
                      return (
                        <tr key={log.id || idx} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 py-3 text-center text-gray-400 font-mono">{idx + 1}</td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="font-mono font-semibold text-gray-900">{timeStr}</div>
                            <div className="text-[10px] text-gray-400">{dateStr}</div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {log.eventType === 'login' ? (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
                                <span>🔑</span>
                                <span>Student Login</span>
                              </span>
                            ) : log.eventType === 'session_start' ? (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-sky-100 text-sky-800 border border-sky-300">
                                <span>🚀</span>
                                <span>Classroom Entry</span>
                              </span>
                            ) : log.eventType === 'heartbeat' ? (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                <span>💓</span>
                                <span>Active Pulse</span>
                              </span>
                            ) : log.eventType === 'tab_visible' ? (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-teal-100 text-teal-800 border border-teal-300">
                                <span>👁️</span>
                                <span>Tab Foreground</span>
                              </span>
                            ) : log.eventType === 'tab_hidden' ? (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                                <span>💤</span>
                                <span>Tab Background</span>
                              </span>
                            ) : log.eventType === 'page_leave' ? (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-300">
                                <span>🚪</span>
                                <span>Tab Closed</span>
                              </span>
                            ) : log.eventType === 'logout' ? (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-800 border border-red-300">
                                <span>🔒</span>
                                <span>Logged Out</span>
                              </span>
                            ) : log.eventType === 'offline_flush' ? (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-purple-100 text-purple-800 border border-purple-300">
                                <span>⚡</span>
                                <span>Offline Synced</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-800">
                                <span>📌</span>
                                <span>{log.eventType}</span>
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">{log.studentName}</div>
                            {log.batchLabel && (
                              <div className="text-[10px] text-gray-400">Batch: {log.batchLabel}</div>
                            )}
                          </td>
                          <td className="px-3 py-3 font-mono text-gray-700 whitespace-nowrap">
                            <span className="font-semibold">{log.registrationNumber}</span>
                            <span className="text-gray-400 text-[10px] ml-1">({log.regNoSuffix})</span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {vidId ? (
                              <div className="flex items-center space-x-1">
                                <a
                                  href={`https://www.youtube.com/watch?v=${vidId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center space-x-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition"
                                  title="Open YouTube video"
                                >
                                  <span>📹</span>
                                  <span>{vidId}</span>
                                </a>
                                <button
                                  type="button"
                                  onClick={() => setPreviewVideoUrl(`https://www.youtube.com/watch?v=${vidId}`)}
                                  className="text-gray-400 hover:text-gray-700 text-[10px]"
                                  title="Quick Preview"
                                >
                                  👁️
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 font-mono text-[10px]">General</span>
                            )}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {log.incrementalSeconds ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                +{log.incrementalSeconds}s
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-gray-600 max-w-[200px] truncate" title={log.liveLabel || 'Live Training'}>
                            {log.liveLabel || 'Live Training'}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            {log.visibilityState === 'visible' ? (
                              <span className="inline-flex items-center space-x-1 text-[11px] font-semibold text-emerald-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                <span>Visible</span>
                              </span>
                            ) : log.visibilityState === 'hidden' ? (
                              <span className="inline-flex items-center space-x-1 text-[11px] font-semibold text-amber-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                <span>Hidden / Bg</span>
                              </span>
                            ) : (
                              <span className="text-gray-400 text-[11px]">{log.visibilityState || '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-3 max-w-[200px] truncate text-[11px] text-gray-500" title={log.notes || log.userAgent || ''}>
                            {log.notes || log.userAgent || '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="p-3 bg-gray-50/60 border-t border-gray-200 text-xs text-gray-500 flex justify-between items-center">
              <span>Showing <strong>{filteredAuditLogs.length}</strong> activity pulse entries</span>
              <span>Sorted chronologically (latest first)</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Table View: Mode 4: Segmented YouTube Video Classes ── */}
      {viewMode === 'videos' && (
        <div className="space-y-4">
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-rose-900 via-slate-900 to-rose-950 text-white rounded-3xl p-5 sm:p-6 shadow-xl border border-rose-800/40 relative overflow-hidden">
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 mb-2">
                  <VideoCameraIcon className="w-3.5 h-3.5 mr-1" />
                  <span>YouTube Stream Segmentation Architecture</span>
                </span>
                <h3 className="text-xl sm:text-2xl font-black tracking-tight text-white">
                  Segmented Video Classes &amp; Isolated Lecture Rooms
                </h3>
                <p className="text-xs sm:text-sm text-rose-200/80 mt-1 max-w-2xl">
                  Each YouTube video link functions as an independent class session with its own distinct attendance records, real-time classroom chat history, and granular view log trail.
                </p>
              </div>

              {/* Quick Summary Badges */}
              <div className="flex items-center space-x-3 flex-shrink-0">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/10 text-center min-w-[100px]">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-rose-300">Total Videos</p>
                  <p className="text-2xl font-black text-white">{videoSegments.length}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/10 text-center min-w-[100px]">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-rose-300">Total Attended</p>
                  <p className="text-2xl font-black text-white">
                    {videoSegments.reduce((sum, v) => sum + (v.attendeesCount || 0), 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Search Bar for Videos */}
          <div className="bg-white border border-gray-200 rounded-2xl p-3 sm:p-4 shadow-sm flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                <MagnifyingGlassIcon className="w-4 h-4" />
              </div>
              <input
                type="text"
                value={videoSearchQuery}
                onChange={(e) => setVideoSearchQuery(e.target.value)}
                placeholder="Search segmented classes by lecture title, topic, YouTube Video ID, or instructor..."
                className="w-full pl-10 pr-9 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-500"
              />
              {videoSearchQuery && (
                <button
                  type="button"
                  onClick={() => setVideoSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            <span className="text-xs text-gray-500 flex-shrink-0 self-center">
              Showing <strong>{filteredVideoSegments.length}</strong> of <strong>{videoSegments.length}</strong> video classes
            </span>
          </div>

          {/* Video Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredVideoSegments.map((seg) => {
              const thumbUrl = formatYouTubeThumbnailUrl(seg.videoId, 'hqdefault');

              return (
                <div
                  key={seg.videoId}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all flex flex-col group border-t-4 border-t-rose-500"
                >
                  {/* Thumbnail & Video Preview Button */}
                  <div className="relative aspect-video bg-slate-900 overflow-hidden group/thumb">
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt={seg.title}
                        className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-800 text-slate-400">
                        <VideoCameraIcon className="w-12 h-12 opacity-40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    
                    {/* Video ID Badge */}
                    <div className="absolute top-2.5 left-2.5 flex items-center space-x-1.5">
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-bold bg-black/70 text-white backdrop-blur-md border border-white/20">
                        ID: {seg.videoId}
                      </span>
                      {seg.date && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-600/90 text-white backdrop-blur-md">
                          {seg.date}
                        </span>
                      )}
                    </div>

                    {/* Preview Play Trigger */}
                    <button
                      type="button"
                      onClick={() => setPreviewVideoUrl(`https://www.youtube.com/watch?v=${seg.videoId}`)}
                      className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-rose-600/90 text-white flex items-center justify-center shadow-xl hover:bg-rose-500 hover:scale-110 transition-all opacity-80 group-hover/thumb:opacity-100"
                      title="Preview Video Player"
                    >
                      <PlayIcon className="w-6 h-6 ml-0.5" />
                    </button>

                    <div className="absolute bottom-2 left-2.5 right-2.5 flex items-center justify-between text-white text-[11px]">
                      <span className="truncate max-w-[200px] text-gray-200 font-medium">
                        {seg.instructor || 'Gauri Tiwari'}
                      </span>
                      <a
                        href={seg.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-rose-300 hover:text-white flex items-center space-x-0.5 font-bold"
                      >
                        <span>YouTube</span>
                        <span>↗</span>
                      </a>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <h4 className="font-bold text-sm text-gray-900 line-clamp-2 tracking-tight group-hover:text-rose-600 transition-colors">
                        {seg.title}
                      </h4>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                        Topic: <span className="font-medium text-gray-700">{seg.topic || 'General Live Lecture'}</span>
                      </p>
                    </div>

                    {/* 4 Segmented Metrics */}
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                      <div className="bg-gray-50 p-2.5 rounded-xl text-center">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Attendees</p>
                        <p className="text-base font-black text-gray-900 mt-0.5">{seg.attendeesCount || 0}</p>
                      </div>
                      <div className="bg-gray-50 p-2.5 rounded-xl text-center">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Watch Time</p>
                        <p className="text-base font-black text-emerald-700 mt-0.5">{formatSeconds(seg.totalWatchSeconds || 0)}</p>
                      </div>
                      <div className="bg-gray-50 p-2.5 rounded-xl text-center">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">View Logs</p>
                        <p className="text-base font-black text-indigo-700 mt-0.5">{seg.viewLogsCount || 0}</p>
                      </div>
                      <div className="bg-gray-50 p-2.5 rounded-xl text-center">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Isolated Room</p>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 mt-0.5">
                          Active
                        </span>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-2 flex flex-col space-y-1.5">
                      <button
                        type="button"
                        onClick={() => handleInspectVideo(seg, 'overview')}
                        className="w-full py-2 px-3 rounded-xl bg-gray-900 hover:bg-black text-white text-xs font-bold transition flex items-center justify-center space-x-1.5 shadow-sm"
                      >
                        <EyeIcon className="w-4 h-4 text-rose-400" />
                        <span>Inspect Video Class &amp; History</span>
                      </button>

                      <div className="grid grid-cols-3 gap-1 text-[10px]">
                        <button
                          type="button"
                          onClick={() => {
                            const segRecords = records.filter(
                              (r) => (r.videoId || extractYouTubeVideoId(r.streamUrl)) === seg.videoId
                            );
                            exportVideoAttendanceToExcel(segRecords, {
                              videoId: seg.videoId,
                              videoUrl: seg.videoUrl,
                              sessionName: seg.title,
                            });
                          }}
                          className="py-1.5 px-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-semibold rounded-lg border border-emerald-200 text-center transition"
                          title="Export Attendance for this video"
                        >
                          📥 Attendance
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInspectVideo(seg, 'chat')}
                          className="py-1.5 px-2 bg-blue-50 hover:bg-blue-100 text-blue-800 font-semibold rounded-lg border border-blue-200 text-center transition"
                          title="Inspect and export Chat for this video"
                        >
                          💬 Chat
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const segLogs = auditLogs.filter(
                              (l) => (l.videoId || extractYouTubeVideoId(l.streamUrl)) === seg.videoId
                            );
                            exportVideoAuditLogsToExcel(segLogs, {
                              videoId: seg.videoId,
                              videoUrl: seg.videoUrl,
                              sessionName: seg.title,
                            });
                          }}
                          className="py-1.5 px-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-800 font-semibold rounded-lg border border-indigo-200 text-center transition"
                          title="Export View Logs for this video"
                        >
                          📜 View Logs
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Student History Breakdown Modal ── */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white border border-gray-200 rounded-3xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-gray-200 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between gap-3 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 border border-indigo-400/40 flex items-center justify-center text-indigo-300 font-bold">
                  🎓
                </div>
                <div>
                  <h3 className="font-bold text-base sm:text-lg text-white">
                    {selectedUser.studentName}
                  </h3>
                  <div className="flex items-center space-x-2 text-xs text-indigo-200 font-mono mt-0.5">
                    <span>Reg No: {selectedUser.registrationNumber}</span>
                    <span>&bull;</span>
                    <span>ID: {selectedUser.regNoSuffix}</span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedUser(null)}
                className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 p-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              <div className="bg-white p-3 rounded-xl border border-gray-200 text-center">
                <p className="text-[10px] font-bold uppercase text-emerald-700">Today</p>
                <p className="text-lg font-black text-gray-900 mt-0.5">
                  {formatSeconds(selectedUser.dailySeconds)}
                </p>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-200 text-center">
                <p className="text-[10px] font-bold uppercase text-purple-700">Past 7 Days</p>
                <p className="text-lg font-black text-gray-900 mt-0.5">
                  {formatSeconds(selectedUser.weeklySeconds)}
                </p>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-200 text-center">
                <p className="text-[10px] font-bold uppercase text-amber-800">Past 30 Days</p>
                <p className="text-lg font-black text-gray-900 mt-0.5">
                  {formatSeconds(selectedUser.monthlySeconds)}
                </p>
              </div>
              <div className="bg-white p-3 rounded-xl border border-gray-200 text-center">
                <p className="text-[10px] font-bold uppercase text-blue-700">Total Lifetime</p>
                <p className="text-lg font-black text-gray-900 mt-0.5">
                  {formatSeconds(selectedUser.totalSeconds)}
                </p>
                <p className="text-[9px] text-gray-500">{selectedUser.daysAttended} sessions</p>
              </div>
            </div>

            {/* Modal Session Table */}
            <div className="flex-1 overflow-y-auto p-4">
              <h4 className="font-bold text-xs text-gray-700 uppercase tracking-wider mb-2.5">
                Session Log History ({selectedUser.records.length} Recorded Sessions)
              </h4>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-left text-xs divide-y divide-gray-200">
                  <thead className="bg-gray-50 text-gray-600 font-bold uppercase text-[10px]">
                    <tr>
                      <th className="px-3 py-2.5">Date</th>
                      <th className="px-3 py-2.5">Lecture / Topic</th>
                      <th className="px-3 py-2.5">First Joined</th>
                      <th className="px-3 py-2.5">Last Seen</th>
                      <th className="px-3 py-2.5">Duration</th>
                      <th className="px-3 py-2.5 text-center">Heartbeats</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {selectedUser.records.map((r, i) => (
                      <tr key={r.id || i} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2.5 font-semibold text-gray-900 whitespace-nowrap">
                          {r.sessionDate}
                        </td>
                        <td className="px-3 py-2.5 text-gray-600 max-w-[200px] truncate">
                          {r.liveLabel || 'Live Training Session'}
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                          {formatTimeOnly(r.firstJoinedAt)}
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                          {formatTimeOnly(r.lastSeenAt)}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                            {formatSeconds(r.totalWatchSeconds || 0)}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-center font-mono text-gray-500">
                          {r.heartbeatCount || 1}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-3 sm:p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-gray-500">
                First joined: <strong>{selectedUser.firstJoinedDate}</strong> &bull; Last seen: <strong>{selectedUser.lastSeenDate}</strong>
              </span>
              <button
                type="button"
                onClick={() => setSelectedUser(null)}
                className="px-4 py-1.5 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold text-xs transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Segmented Video Inspection Modal ── */}
      {inspectingVideo && (() => {
        const segRecords = records.filter(
          (r) => (r.videoId || extractYouTubeVideoId(r.streamUrl)) === inspectingVideo.videoId
        );
        const segLogs = auditLogs.filter(
          (l) => (l.videoId || extractYouTubeVideoId(l.streamUrl)) === inspectingVideo.videoId
        );
        const totalSecs = segRecords.reduce((sum, r) => sum + (r.totalWatchSeconds || 0), 0);

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-md animate-fadeIn">
            <div className="bg-white border border-gray-200 rounded-3xl max-w-4xl w-full max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
              {/* Modal Header */}
              <div className="p-4 sm:p-5 border-b border-gray-200 bg-gradient-to-r from-rose-950 via-slate-900 to-rose-950 text-white flex items-center justify-between gap-3 flex-shrink-0">
                <div className="flex items-center space-x-3 min-w-0">
                  <div className="w-10 h-10 rounded-2xl bg-rose-500/20 border border-rose-400/40 flex items-center justify-center text-rose-300 font-bold flex-shrink-0">
                    📹
                  </div>
                  <div className="truncate">
                    <div className="flex items-center space-x-2">
                      <h3 className="font-bold text-base sm:text-lg text-white truncate">
                        {inspectingVideo.title}
                      </h3>
                      <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-rose-600 text-white flex-shrink-0">
                        {inspectingVideo.videoId}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-rose-200 font-medium mt-0.5 truncate">
                      <span>Instructor: {inspectingVideo.instructor || 'Gauri Tiwari'}</span>
                      {inspectingVideo.date && (
                        <>
                          <span>&bull;</span>
                          <span>Date: {inspectingVideo.date}</span>
                        </>
                      )}
                      <span>&bull;</span>
                      <a
                        href={inspectingVideo.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-rose-300 underline hover:text-white"
                      >
                        Open on YouTube ↗
                      </a>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setInspectingVideo(null)}
                  className="p-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white transition flex-shrink-0"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Navigation Tabs */}
              <div className="bg-gray-100 p-2 border-b border-gray-200 flex items-center space-x-1.5 overflow-x-auto flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setInspectingSubTab('overview')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                    inspectingSubTab === 'overview'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  📊 Overview
                </button>
                <button
                  type="button"
                  onClick={() => setInspectingSubTab('attendance')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center space-x-1.5 ${
                    inspectingSubTab === 'attendance'
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : 'text-emerald-700 hover:bg-emerald-50'
                  }`}
                >
                  <span>👥 Video Attendance</span>
                  <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-white/20">{segRecords.length}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInspectingSubTab('chat')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center space-x-1.5 ${
                    inspectingSubTab === 'chat'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-blue-700 hover:bg-blue-50'
                  }`}
                >
                  <span>💬 Video Chat Room</span>
                  <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-white/20">{inspectingChatMessages.length}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInspectingSubTab('logs')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center space-x-1.5 ${
                    inspectingSubTab === 'logs'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-indigo-700 hover:bg-indigo-50'
                  }`}
                >
                  <span>📜 View Log Trail</span>
                  <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-white/20">{segLogs.length}</span>
                </button>
              </div>

              {/* Subtab 1: Overview */}
              {inspectingSubTab === 'overview' && (
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-emerald-50 p-3.5 rounded-2xl border border-emerald-200 text-center">
                      <p className="text-[10px] font-bold uppercase text-emerald-700">Attendees</p>
                      <p className="text-2xl font-black text-emerald-900 mt-0.5">{segRecords.length}</p>
                      <p className="text-[10px] text-emerald-600">Students attended</p>
                    </div>
                    <div className="bg-blue-50 p-3.5 rounded-2xl border border-blue-200 text-center">
                      <p className="text-[10px] font-bold uppercase text-blue-700">Watch Duration</p>
                      <p className="text-2xl font-black text-blue-900 mt-0.5">{formatSeconds(totalSecs)}</p>
                      <p className="text-[10px] text-blue-600">{Math.round(totalSecs / 60)} active mins</p>
                    </div>
                    <div className="bg-purple-50 p-3.5 rounded-2xl border border-purple-200 text-center">
                      <p className="text-[10px] font-bold uppercase text-purple-700">Chat Messages</p>
                      <p className="text-2xl font-black text-purple-900 mt-0.5">{inspectingChatMessages.length}</p>
                      <p className="text-[10px] text-purple-600">Dedicated room</p>
                    </div>
                    <div className="bg-indigo-50 p-3.5 rounded-2xl border border-indigo-200 text-center">
                      <p className="text-[10px] font-bold uppercase text-indigo-700">View Log Pulses</p>
                      <p className="text-2xl font-black text-indigo-900 mt-0.5">{segLogs.length}</p>
                      <p className="text-[10px] text-indigo-600">Heartbeats &amp; joins</p>
                    </div>
                  </div>

                  {/* Video Thumbnail Preview & Details */}
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 flex flex-col md:flex-row gap-4 items-center">
                    <div className="relative w-full md:w-64 aspect-video bg-black rounded-xl overflow-hidden flex-shrink-0 group">
                      <img
                        src={formatYouTubeThumbnailUrl(inspectingVideo.videoId, 'hqdefault')}
                        alt={inspectingVideo.title}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setPreviewVideoUrl(`https://www.youtube.com/watch?v=${inspectingVideo.videoId}`)}
                        className="absolute inset-0 m-auto w-10 h-10 rounded-full bg-rose-600 text-white flex items-center justify-center shadow-lg hover:scale-110 transition"
                      >
                        <PlayIcon className="w-5 h-5 ml-0.5" />
                      </button>
                    </div>

                    <div className="flex-1 space-y-2 text-xs">
                      <h4 className="font-bold text-base text-gray-900">{inspectingVideo.title}</h4>
                      <p className="text-gray-600">
                        <strong>Topic Covered:</strong> {inspectingVideo.topic || 'General Athletics Fundamentals'}
                      </p>
                      <p className="text-gray-600">
                        <strong>Assigned Faculty:</strong> {inspectingVideo.instructor || 'Gauri Tiwari'}
                      </p>
                      <p className="text-gray-600 font-mono text-[11px] truncate">
                        <strong>YouTube URL:</strong> {inspectingVideo.videoUrl}
                      </p>
                    </div>
                  </div>

                  {/* Export Actions Bar */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <span className="text-xs font-bold text-gray-700">Export Video Reports to Excel:</span>
                    <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                      <button
                        type="button"
                        onClick={() => exportVideoAttendanceToExcel(segRecords, inspectingVideo)}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition"
                      >
                        📥 Export Attendance ({segRecords.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => exportVideoChatMessagesToExcel(inspectingChatMessages, inspectingVideo)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-sm transition"
                      >
                        💬 Export Chat ({inspectingChatMessages.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => exportVideoAuditLogsToExcel(segLogs, inspectingVideo)}
                        className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition"
                      >
                        📜 Export View Logs ({segLogs.length})
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Subtab 2: Video Attendance */}
              {inspectingSubTab === 'attendance' && (
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 font-semibold">
                      Showing <strong>{segRecords.length}</strong> students who attended this YouTube video
                    </span>
                    <button
                      type="button"
                      onClick={() => exportVideoAttendanceToExcel(segRecords, inspectingVideo)}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition"
                    >
                      📥 Export Video Attendance
                    </button>
                  </div>

                  <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <table className="w-full text-left text-xs divide-y divide-gray-200">
                      <thead className="bg-gray-50 text-gray-700 font-bold uppercase text-[10px]">
                        <tr>
                          <th className="px-3 py-3 w-12 text-center">#</th>
                          <th className="px-4 py-3">Student Name</th>
                          <th className="px-3 py-3">Reg No</th>
                          <th className="px-3 py-3">First Joined</th>
                          <th className="px-3 py-3">Last Active</th>
                          <th className="px-3 py-3">Total Watch Time</th>
                          <th className="px-3 py-3 text-center">Heartbeats</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-gray-700">
                        {segRecords.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                              No attendance records logged for this video yet.
                            </td>
                          </tr>
                        ) : (
                          segRecords.map((r, i) => (
                            <tr key={r.id || i} className="hover:bg-gray-50 transition-colors">
                              <td className="px-3 py-3 text-center text-gray-400">{i + 1}</td>
                              <td className="px-4 py-3">
                                <div className="font-semibold text-gray-900">{r.studentName}</div>
                                <div className="text-[10px] text-gray-400 font-mono">ID: {r.regNoSuffix}</div>
                              </td>
                              <td className="px-3 py-3 font-mono text-gray-600">{r.registrationNumber}</td>
                              <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{formatTimeOnly(r.firstJoinedAt)}</td>
                              <td className="px-3 py-3 text-gray-500 whitespace-nowrap">{formatTimeOnly(r.lastSeenAt)}</td>
                              <td className="px-3 py-3">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">
                                  {formatSeconds(r.totalWatchSeconds || 0)}
                                </span>
                              </td>
                              <td className="px-3 py-3 text-center font-mono text-gray-500">{r.heartbeatCount || 1}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Subtab 3: Video Chat History */}
              {inspectingSubTab === 'chat' && (
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 font-semibold">
                      Isolated Chat Room: <strong>{inspectingChatMessages.length}</strong> messages
                    </span>
                    <button
                      type="button"
                      onClick={() => exportVideoChatMessagesToExcel(inspectingChatMessages, inspectingVideo)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-sm transition"
                    >
                      📥 Export Video Chat
                    </button>
                  </div>

                  {loadingVideoChat ? (
                    <div className="p-12 text-center text-gray-500 text-xs">Loading chat messages...</div>
                  ) : inspectingChatMessages.length === 0 ? (
                    <div className="p-12 text-center text-gray-500 text-xs bg-gray-50 rounded-2xl border border-gray-200">
                      No chat messages sent in this video room yet.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                      {inspectingChatMessages.map((m, i) => (
                        <div
                          key={m.id || i}
                          className={`p-3 rounded-2xl text-xs border ${
                            m.senderRole === 'instructor' || m.senderRole === 'admin'
                              ? 'bg-amber-50/70 border-amber-200 text-amber-950'
                              : 'bg-white border-gray-200 text-gray-800 shadow-sm'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                            <div className="flex items-center space-x-1.5 flex-wrap">
                              <span className="font-bold text-gray-900">{m.senderName}</span>
                              <span className={`px-1.5 py-0.2 rounded text-[10px] font-bold ${
                                m.senderRole === 'instructor'
                                  ? 'bg-amber-200 text-amber-900'
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {m.badgeLabel || m.senderRole}
                              </span>
                              {m.isPrivate ? (
                                <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-purple-100 text-purple-800 border border-purple-200">
                                  🔒 1-to-1 Private {m.recipientName ? `to ${m.recipientName}` : ''}
                                </span>
                              ) : m.senderRole === 'instructor' ? (
                                <span className="px-1.5 py-0.2 rounded text-[9px] font-medium bg-emerald-100 text-emerald-800">
                                  📢 Broadcast to All
                                </span>
                              ) : (
                                <span className="px-1.5 py-0.2 rounded text-[9px] font-medium bg-gray-100 text-gray-600">
                                  Student Query
                                </span>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-400 font-mono">
                              {m.createdAt?.toDate ? m.createdAt.toDate().toLocaleTimeString('en-IN') : ''}
                            </span>
                          </div>
                          {m.replyToText && (
                            <div className="mb-1.5 p-1.5 bg-gray-100 rounded-lg text-[10px] text-gray-600 italic border-l-2 border-amber-400">
                              "{m.replyToText}"
                            </div>
                          )}
                          <p className="text-gray-700 whitespace-pre-wrap">{m.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Subtab 4: View Log Trail */}
              {inspectingSubTab === 'logs' && (
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600 font-semibold">
                      Showing <strong>{segLogs.length}</strong> activity pulses &amp; view logs for this video
                    </span>
                    <button
                      type="button"
                      onClick={() => exportVideoAuditLogsToExcel(segLogs, inspectingVideo)}
                      className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-sm transition"
                    >
                      📥 Export Video View Logs
                    </button>
                  </div>

                  <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <table className="w-full text-left text-xs divide-y divide-gray-200">
                      <thead className="bg-gray-50 text-gray-700 font-bold uppercase text-[10px]">
                        <tr>
                          <th className="px-3 py-3 w-12 text-center">#</th>
                          <th className="px-3 py-3 whitespace-nowrap">Timestamp</th>
                          <th className="px-3 py-3">Event</th>
                          <th className="px-4 py-3">Student Name</th>
                          <th className="px-3 py-3">Watch Pulse</th>
                          <th className="px-3 py-3">Visibility</th>
                          <th className="px-3 py-3">Notes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-gray-700">
                        {segLogs.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                              No view log history recorded for this video yet.
                            </td>
                          </tr>
                        ) : (
                          segLogs.map((log, i) => (
                            <tr key={log.id || i} className="hover:bg-gray-50 transition-colors">
                              <td className="px-3 py-3 text-center text-gray-400">{i + 1}</td>
                              <td className="px-3 py-3 whitespace-nowrap font-mono text-[11px] text-gray-800">
                                {log.timestampStr || (log.timestamp?.toDate ? log.timestamp.toDate().toLocaleTimeString('en-IN') : '')}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-800">
                                  {log.eventType}
                                </span>
                              </td>
                              <td className="px-4 py-3 font-semibold text-gray-900">{log.studentName}</td>
                              <td className="px-3 py-3">
                                {log.incrementalSeconds ? (
                                  <span className="font-bold text-emerald-700">+{log.incrementalSeconds}s</span>
                                ) : '—'}
                              </td>
                              <td className="px-3 py-3 text-[11px] text-gray-600">{log.visibilityState || '—'}</td>
                              <td className="px-3 py-3 text-[11px] text-gray-500 truncate max-w-xs">{log.notes || '—'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Modal Footer */}
              <div className="p-3 sm:p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
                <span className="text-xs text-gray-500">
                  Video ID: <strong className="font-mono">{inspectingVideo.videoId}</strong> &bull; Segment Status: <strong className="text-emerald-700">Isolated &amp; Preserved</strong>
                </span>
                <button
                  type="button"
                  onClick={() => setInspectingVideo(null)}
                  className="px-4 py-1.5 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold text-xs transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── YouTube Video Player Preview Modal ── */}
      {previewVideoUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="bg-slate-950 border border-slate-800 rounded-3xl max-w-4xl w-full flex flex-col shadow-2xl overflow-hidden">
            <div className="p-3 sm:p-4 border-b border-slate-800 flex items-center justify-between text-white">
              <span className="font-bold text-sm flex items-center space-x-2">
                <span>📹</span>
                <span>Live Lecture Video Preview</span>
              </span>
              <button
                type="button"
                onClick={() => setPreviewVideoUrl(null)}
                className="p-1 rounded-xl bg-white/10 hover:bg-white/20 text-white transition"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="relative aspect-video w-full bg-black">
              {getYouTubeEmbedUrl(previewVideoUrl) ? (
                <iframe
                  src={getYouTubeEmbedUrl(previewVideoUrl) || ''}
                  title="YouTube Video Preview"
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-500">
                  Invalid YouTube URL
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default UserAttendanceAnalytics;
